/* eslint-disable react-native/no-inline-styles */
// NOTE: This component is used on *WEB* only!

import TextInput from '@Components/Common/TextInput';
import { ProductPanelScrollMarker } from '@Components/ProductDetailPanel/product-panel-scroll-marker';
import { useSideDrawerLeftPresenter } from '@Components/SideDrawerLeft/SideDrawerLeftPresenter';
import { useScreenSize } from '@Hooks/useScreenSize';
import useCheckoutPresenter from '@Screens/Checkout/presenter';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React, { useEffect, useRef } from 'react';
import { ActivityIndicator, TextStyle } from 'react-native';
import styled from 'styled-components/native';

import { defaultInputProps } from '.';
import OwnableTheme from '../../../../assets/OwnableTheme';
import { PrimaryButton } from '../../../Common/PrimaryButton';
import { Text } from '../../../Common/Text';
import { useCheckoutContextState } from '../CheckoutContextState';
import DeliveryAddressSection from './Sections/DeliveryAddressSection';
import PaymentOptionsSection from './Sections/PaymentOptionsSection';

import { useCheckoutStore } from '~stores/checkout';
import { ScreenSize } from '~types/views-types';
import { IS_SMALL_SCREEN } from '@Constants/LeftDrawer';
import { buttonLabelStyles } from '../utils';

const Section = styled.View`
  display: flex;
  width: 100%;
  padding-top: 40px;
  margin-top: 30px;
  border-top-width: 2px;
  border-top-color: #dcdcdc;
`;

const StyledContainer = styled.View`
  align-items: center;
  justify-content: flex-start;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SubmitButtonContainer = styled.View`
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 25px;
`;

const StyledActivityIndicatorContainer = styled.View`
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const StyledButtons = styled.View<{ size: ScreenSize }>`
  display: flex;
  flex-direction: ${(props) =>
    [ScreenSize.mobile, ScreenSize.thinmobile].includes(props.size)
      ? 'column'
      : 'row'}; ;
`;

const StyledButtonsContainer = styled.View`
  margin-bottom: 10px;
`;

const StyledCardDetailsContainer = styled.View`
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
`;

type Props = {
  checkoutIsSubmitting: boolean;
  ConfirmOrder: () => Promise<void>;
};

const SharedComponents: FC<Props> = ({
  checkoutIsSubmitting,
  ConfirmOrder,
}) => {
  const {
    isFeatureAvailableForCheckout,
    submitOrderValidationResult,
    placeOrderButtonText,
    orderErrorMessage,
    _onCancelCheckoutButton,
    checkoutOrderId,
    guestCheckoutName,
    guestCheckoutEmail,
  } = useCheckoutStore();

  const { onCancelButton } = useCheckoutContextState();

  const { activeProductId: productId } = useSideDrawerLeftPresenter();
  const [size] = useScreenSize();

  const previousProductId = useRef(productId);

  useEffect(() => {
    if (productId !== previousProductId.current) {
      _onCancelCheckoutButton();
      previousProductId.current = productId;
    }
  }, [productId]);

  const {
    handlePlaceOrderPress,
    orderDetailsVisible,
    setGuestCheckoutName,
    setGuestCheckoutEmail,
  } = useCheckoutPresenter();

  if (checkoutOrderId) {
    return null;
  }

  return (
    <StyledContainer>
      <ProductPanelScrollMarker autoFocus />
      <DeliveryAddressSection checkoutIsSubmitting={checkoutIsSubmitting} />
      <PaymentOptionsSection checkoutIsSubmitting={checkoutIsSubmitting} />

      <Section>
        {isFeatureAvailableForCheckout('CollectGuestName') && (
          <StyledCardDetailsContainer>
            <Text
              color={OwnableTheme.colors.black}
              weight='bold'
              gutterBottom={20}
              size='m'
              align='left'
            >
              Enter your details
            </Text>
            <TextInput
              {...defaultInputProps}
              textAlign='left'
              value={guestCheckoutName}
              placeholder='Your Name'
              onChange={(text) => {
                setGuestCheckoutName(text);
              }}
              borderColor={
                (submitOrderValidationResult?.affectedFields ?? []).includes(
                  'GUEST_CHECKOUT_NAME'
                )
                  ? 'errorRed'
                  : 'palePersianIndigo'
              }
              id='name'
              name='name'
              autocomplete='name'
            />
          </StyledCardDetailsContainer>
        )}
        {isFeatureAvailableForCheckout('CollectGuestEmail') && (
          <TextInput
            {...defaultInputProps}
            textAlign='left'
            value={guestCheckoutEmail}
            type='email'
            placeholder='Your Email Address'
            onChange={(text) => {
              setGuestCheckoutEmail(text);
            }}
            borderColor={
              (submitOrderValidationResult?.affectedFields ?? []).includes(
                'GUEST_CHECKOUT_EMAIL'
              )
                ? 'errorRed'
                : 'palePersianIndigo'
            }
            id='email'
            name='email'
            autocomplete='email'
          />
        )}
      </Section>
      {!submitOrderValidationResult?.result && (
        <Text
          color={OwnableTheme.colors.errorRed}
          size='s'
          weight='regular'
          fontStyleOverride={buttonLabelStyles}
        >
          {submitOrderValidationResult?.reason?.reason}
        </Text>
      )}

      {Boolean(orderErrorMessage) && (
        <Text
          color={OwnableTheme.colors.errorRed}
          size='s'
          weight='regular'
          fontStyleOverride={buttonLabelStyles}
        >
          {orderErrorMessage}
        </Text>
      )}

      {isFeatureAvailableForCheckout('ConfirmOrderButton') && (
        <SubmitButtonContainer>
          {checkoutIsSubmitting ? (
            <StyledActivityIndicatorContainer>
              <ActivityIndicator
                color={OwnableTheme.colors.black}
                size='small'
              />
            </StyledActivityIndicatorContainer>
          ) : (
            <StyledButtons size={size}>
              <StyledButtonsContainer>
                <PrimaryButton
                  text={placeOrderButtonText}
                  color='black'
                  textColor='white'
                  fontStyleOverride={buttonLabelStyles}
                  onPress={() => handlePlaceOrderPress(ConfirmOrder)}
                  padding={70}
                />
              </StyledButtonsContainer>
              <StyledButtonsContainer>
                <PrimaryButton
                  width={220}
                  text='Cancel'
                  color='white'
                  textColor='black'
                  borderColor='black'
                  fontStyleOverride={buttonLabelStyles}
                  onPress={() => {
                    orderDetailsVisible.set(false);
                    onCancelButton();
                    _onCancelCheckoutButton();
                  }}
                />
              </StyledButtonsContainer>
            </StyledButtons>
          )}
        </SubmitButtonContainer>
      )}
    </StyledContainer>
  );
};

export default observer(SharedComponents);
